import * as React from 'react'
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik'
import { Input, Select } from '@mobiscroll/react-latest'
import * as Yup from 'yup'
import * as classNames from 'classnames'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import { IMap, IOrgObj, IStationUserUpdateDTO, IStationUserSearchObject } from '../../../types'
import ImageUpload from 'src/components/ui/ImageUpload'
import Divider from 'src/components/ui/Divider'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import UIMessages from 'src/utils/ui-messages'
import PreloaderCircle from 'src/components/ui/PreloaderCircle'

export interface IFormValues extends IFormValuesSubmit {
  formError?: string
}

export interface IFormValuesSubmit {
  email: string
  username: string
  firstName: string
  lastName: string
  phoneNumber: string
  organizationId: string
}

interface IFormProps<T = Formik> {
  formRef: React.RefObject<T>
  isEdit: boolean
  isLoading: boolean
  organizations: IMap<IOrgObj>
  selectedOrg?: IOrgObj
  stationUser: IStationUserSearchObject
  onSubmit: (stationUser: IFormValuesSubmit) => Promise<any>
  onUpdate: (stationUser: IStationUserUpdateDTO) => Promise<any>
}

const InnerForm = (
  isNew: boolean,
  organizations: IMap<IOrgObj>,
  selectOrg: (e: any) => void,
  selectedOrgId: string,
  imageUploadComp: JSX.Element | React.ReactNode,
  isLoading: boolean
) => (props: IFormProps & FormikProps<IFormValues>): any => {
  const { values, touched, errors, handleSubmit, handleChange, handleBlur, setFieldTouched, setFieldValue } = props
  const {
    common: { personalInfo, tips },
    stationUser: { stationUserTips },
    teamMember: {
      form: {
        email: emailLabel,
        firstName: firstNameLabel,
        lastName: lastNameLabel,
        organization: organizationLabel,
        username: usernameLabel
      }
    }
  } = UIMessages

  const inputProps = {
    inputStyle: 'box' as const,
    labelStyle: 'floating' as const,
    theme: 'ios'
  }

  const { email: emailErr, firstName: firstNameErr, lastName: lastNameErr, username: usernameErr } = errors

  const {
    email: emailTouched,
    firstName: firstNameTouched,
    lastName: lastNameTouched,
    username: usernameTouched
  } = touched
  // const orgOptions = Object.values(organizations).map(o => ({ label: o.name, value: o.id }))

  const dropDownValue = organizations[selectedOrgId] ? selectedOrgId : Object.keys(organizations)[0]

  const orgOptions = organizations[selectedOrgId]
    ? [{ value: selectedOrgId, text: organizations[selectedOrgId].name }]
    : []

  const handlePhoneField = (value: any) => {
    setFieldTouched('phoneNumber')
    setFieldValue('phoneNumber', value)
  }

  const textInput = (value: FieldProps) => {
    const { field } = value

    const inputDetails = {
      username: {
        error: usernameErr && usernameTouched,
        errorMessage: usernameErr,
        label: `${usernameLabel}*`,
        required: true,
        type: 'text',
        onBlur: handleBlur,
        onChange: handleChange
      },
      firstName: {
        error: firstNameErr && firstNameTouched,
        errorMessage: firstNameErr,
        label: `${firstNameLabel}*`,
        required: true,
        type: 'text',
        onBlur: handleBlur,
        onChange: handleChange
      },
      lastName: {
        error: lastNameErr && lastNameTouched,
        errorMessage: lastNameErr,
        label: `${lastNameLabel}*`,
        required: true,
        type: 'text',
        onBlur: handleBlur,
        onChange: handleChange
      },
      email: {
        error: emailErr && emailTouched,
        errorMessage: emailErr,
        label: emailLabel,
        required: false,
        type: 'email',
        onBlur: handleBlur,
        onChange: handleChange
      }
    }
    const textInputProps = inputDetails[field.name as keyof typeof inputDetails]
    return <Input {...field} {...inputProps} {...textInputProps} />
  }

  return (
    <section className='su-personal-info-form-container'>
      <FloatingForm>
        {isLoading ? (
          <div className='flex justify-center w100'>
            <PreloaderCircle loading={isLoading} />
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <div className='mbsc-grid no-padding mb3'>
              <div className='mbsc-row'>
                <div className='mbsc-col-12 mbsc-col-lg-8 mbsc-padding'>
                  <p className='bold font-grey3'>{personalInfo}</p>
                  <Divider />
                  <div className='mbsc-row'>
                    <div className='mbsc-col-12 mbsc-padding'>{imageUploadComp}</div>
                    <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                      <Select
                        {...inputProps}
                        data={orgOptions}
                        label={organizationLabel}
                        value={dropDownValue}
                        onChange={selectOrg}
                      />
                    </div>
                    <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                      <Field name='username' render={textInput} />
                    </div>
                    <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                      <Field name='firstName' render={textInput} />
                    </div>
                    <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                      <Field name='lastName' render={textInput} />
                    </div>
                    <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                      <Field name='email' render={textInput} />
                    </div>
                    <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                      <PhoneInput
                        className={classNames('phone-input', {
                          'border-red :focus-red': !!(touched.phoneNumber && errors.phoneNumber)
                        })}
                        name='phoneNumber'
                        placeholder='Phone number'
                        defaultCountry='US'
                        value={values.phoneNumber}
                        onChange={handlePhoneField}
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>
                </div>
                <div className='mbsc-col-12 mbsc-col-lg-4 mbsc-padding'>
                  <p className='bold font-grey3'>{tips}</p>
                  <Divider />
                  <div className='mbsc-row'>
                    <div className='mbsc-col-12 mbsc-padding'>
                      <div className='font-light-grey'>{stationUserTips}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </FloatingForm>
    </section>
  )
}

class StationUserInnerForm extends React.Component<IFormProps> {
  public state = {
    selectedOrgId: this.props.selectedOrg ? this.props.selectedOrg.id : ''
    // selectedOrgId: this.props.stationUser ? this.props.stationUser.organizationId : ''
  }

  private compDivRef: any = null

  private validationSchema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
    email: Yup.string().email('Invalid email address'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    phoneNumber: Yup.string()
      .nullable(true)
      .test('phone-number', 'Phone number is not valid', phoneNumber =>
        phoneNumber ? isValidPhoneNumber(phoneNumber) : true
      )
      // .required('Phone number is required')
      .max(15, 'Phone number is too long')
    // roles: Yup.array().min(1, 'At least one role is required')
  })

  constructor(props: any) {
    super(props)
    this.compDivRef = React.createRef()
  }

  public componentDidUpdate(prevProps: IFormProps) {
    if (this.props.selectedOrg && JSON.stringify(this.props.selectedOrg) !== JSON.stringify(prevProps.selectedOrg)) {
      this.setState({
        selectedOrgId: this.props.selectedOrg.id
      })
    }
  }

  public render() {
    const { stationUser, formRef, selectedOrg, organizations, isLoading } = this.props
    const teamMemberUserId = stationUser ? stationUser.userId : undefined

    const comp = <ImageUpload entityId={teamMemberUserId} ref={this.compDivRef} />

    return (
      <Formik
        ref={formRef}
        enableReinitialize={true}
        initialValues={{
          email: stationUser ? stationUser.email : '',
          username: stationUser ? stationUser.username : '',
          firstName: stationUser ? stationUser.name.split(' ')[0] : '',
          lastName: stationUser ? stationUser.name.split(' ')[1] : '',
          phoneNumber: stationUser ? '+' + stationUser.countryCode + stationUser.phone : ''
        }}
        validationSchema={this.validationSchema}
        onSubmit={this.handleSubmit}
        render={InnerForm(!stationUser, organizations, this.selectOrg, this.state.selectedOrgId, comp, isLoading)}
      />
    )
  }
  private selectOrg = (e: any) => {
    this.setState({ selectedOrgId: e ? e.value : '' })
  }

  private handleSubmit = ({ formError, ...stationUser }: IFormValues, { setSubmitting, setFieldError }: any) => {
    if (this.props.isEdit) {
      this.props
        .onUpdate({
          id: this.props.stationUser.id,
          username: stationUser.username,
          firstName: stationUser.firstName,
          lastName: stationUser.lastName,
          phoneNumber: stationUser.phoneNumber,
          email: stationUser.email,
          countryCode: this.props.stationUser.countryCode,
          organizationId: this.state.selectedOrgId
        })
        .catch((error: string) => {
          setFieldError('formError', 'There was a problem with your request')
          setSubmitting(false)
        })
    } else {
      this.props
        .onSubmit({
          ...stationUser,
          organizationId: this.state.selectedOrgId
        })
        .then((obj: any) => {
          this.compDivRef.current.submit(obj.id).then(() => {
            setSubmitting(false)
          })
        })
        .catch((error: string) => {
          setFieldError('formError', 'There was a problem with your request')
          setSubmitting(false)
        })
    }
  }
}

export default StationUserInnerForm



// WEBPACK FOOTER //
// ./src/components/station-user/form/StationUserInnerForm.tsx